import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { AgGridModule } from 'ag-grid-angular';
import { FilterChangedEvent, GridReadyEvent } from 'ag-grid-community';
import { GridApi, GridOptions } from 'ag-grid-enterprise';
import { Subject, takeUntil } from 'rxjs';

import { PaginationConfig } from 'v2/modules/shared/configs/paginator.config';
import { DirectiveModule } from 'v2/modules/shared/directives/directive.module';
import { IGetApiResponseMeta } from 'v2/modules/shared/interfaces/api-response.interface';
import {
  EOrderType,
  IOrderAgTableLineItemsPageEvent,
  IOrderRowData,
} from 'v2/modules/shared/interfaces/order.interface';
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';
import { PipeModule } from 'v2/modules/shared/pipes/pipe.module';
import { OrderApiService } from 'v2/modules/shared/services/api/order/order-api.service';
import { SharedComponentsModule } from 'v2/shared-components.module';

import { AgGridCaretCellRendererComponent } from '../ag-grid-caret-cell-renderer/ag-grid-caret-cell-renderer.component';

import { OrderLineItemsAgConfig } from './config/order-ag-table-order-line-items-ag.config';

//

@Component({
  selector: 'lum-order-ag-table-order-line-items',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatPaginatorModule,
    DirectiveModule,
    SharedComponentsModule,
    AgGridCaretCellRendererComponent,
    PipeModule,
    MatSnackBarModule,
    MatMenuModule,
  ],
  templateUrl: './order-ag-table-order-line-items.component.html',
  styleUrls: ['./order-ag-table-order-line-items.component.scss'],
})
export class OrderAgTableOrderLineItemsComponent implements OnInit, OnDestroy {
  @Input() rowData :IOrderRowData;
  @Input() loading :boolean;
  @Input() orderType :EOrderType;

  @Output() detailGridReady = new EventEmitter<GridReadyEvent>();
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() pageEvent = new EventEmitter<IOrderAgTableLineItemsPageEvent>();

  paginationConfig = PaginationConfig;
  gridOptions :GridOptions;

  private columnDefs = OrderLineItemsAgConfig.COL_DEFS;
  private gridApi :GridApi;

  readonly #destroy$ = new Subject<void>();

  constructor(
    private orderApiService :OrderApiService,
    private decimalPipe :DecimalPlacesPipe,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.gridOptions = OrderLineItemsAgConfig.gridOptionsFactory({
      columnDefs: this.columnDefs,
      context: { parentComponent: this },
      onDetailGridReady: (event :GridReadyEvent) => {
        this.detailGridReady.emit(event);
      },
      // get the rows for each Detail Grid
      getDetailRowData: (params) => {
        this.orderApiService
          .getLineItemRawProducts({
            lineItemId: params.data.id,
            orderType: this.orderType,
          })
          .pipe(takeUntil(this.#destroy$))
          .subscribe((response) => {
            params.successCallback(
              response.map((row) => ({
                ...row,
                orderItemId: params.data.id,
              })),
            );
          });
      },
    });
  }

  ngOnDestroy() :void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  onGridReady(event :GridReadyEvent) {
    this.gridApi = event.api;

    this.gridReady.emit(event);
  }

  onFilterChanged(e :FilterChangedEvent) :void {
    // Get the current filter model
    const filterModel = this.gridApi.getFilterModel();
    const filters = {};
    Object.keys(filterModel).forEach((key) => {
      filters[key] = filterModel[key].filter;
    });

    const pageEvent = new PageEvent();
    pageEvent.length = this.meta?.total;
    pageEvent.pageSize = this.meta?.per_page;
    pageEvent.pageIndex = 0;
    this.pageEvent.emit({
      ...pageEvent,
      ...filters,
    });
  }

  onPaginationChanged(event :PageEvent) {
    this.pageEvent.emit({
      ...event,
    });
  }

  handleGoToPageInput(inputElement :HTMLInputElement) :void {
    const inputElementValue :number = Number(inputElement.value);
    if (inputElementValue === 0) {
      inputElement.value = '1';
    }
    if (inputElementValue > this.rowData?.meta?.last_page) {
      inputElement.value = this.rowData?.meta?.last_page.toString();
    }
  }

  gotoPage(pageValue :string) :void {
    if (pageValue === '') {
      return;
    }
    const pageValueInput = Number(pageValue);
    const pageEvent = new PageEvent();
    pageEvent.pageIndex = pageValueInput - 1; // because pageIndex is 0-based
    pageEvent.pageSize = this.meta?.per_page;
    pageEvent.length = this.meta?.total;
    this.pageEvent.emit({
      ...pageEvent,
    });
  }

  get meta() :IGetApiResponseMeta {
    return this.rowData?.meta;
  }
}
